import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

/**
 * Email-link landing page for COPPA verifiable parental consent.
 *
 * URL: /consent/parental/verify?token=...
 *
 * Flow:
 *   - mount → read ?token from URL. If missing/empty, render brokenLink state.
 *   - render 3-question KBA form: child first name, attesting adult name,
 *     age-18 checkbox, full-attestation checkbox.
 *   - submit → POST /api/consent/parental/verify with all of the above.
 *   - response routing per Praxis spec:
 *       200       → "success" celebratory screen, return to localStorage.parental_consent_return
 *       400       → wrong child name inline under Q1 input; token NOT burned, parent can retry
 *       403       → "wrong account" — sign out + sign back in CTA
 *       404       → "broken link"
 *       409       → "already used" — direct to /profiles
 *       410       → "expired" — direct back to dashboard, suggest re-initiate
 *       any other → friendly fallback
 */
export default function ParentalConsentVerify() {
  useSEO({ title: "Parental consent verification", noindex: true });
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") || "").trim();

  const [childName, setChildName] = useState("");
  const [adultName, setAdultName] = useState("");
  const [attestAge, setAttestAge] = useState(false);
  const [attestText, setAttestText] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("form"); // form | success | broken | already_used | expired | wrong_account
  const [childNameError, setChildNameError] = useState("");
  const [genericError, setGenericError] = useState("");
  const [consentId, setConsentId] = useState(null);

  // Locked attestation copy — kept here AND on the consent row server-side.
  // If counsel rewords later, both move together.
  const ATTESTATION = (
    "I am the parent or legal guardian of the child named above. I " +
    "understand SonSpeak will process my child's first name and any " +
    "optional photo or voice recording I choose to add, for the purpose " +
    "of helping my child communicate. I confirm I am 18 years of age or " +
    "older. I understand that knowingly making a false attestation is a " +
    "violation of the Children's Online Privacy Protection Act (COPPA)."
  );

  useEffect(() => {
    if (!token || token.length < 8) setStage("broken");
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setChildNameError("");
    setGenericError("");
    if (childName.trim().length < 1) { setChildNameError("Please enter your child's first name."); return; }
    if (adultName.trim().length < 2) { setGenericError("Please enter your full legal name."); return; }
    if (!attestAge) { setGenericError("Please confirm you are 18 or older."); return; }
    if (!attestText) { setGenericError("Please accept the attestation to continue."); return; }

    setBusy(true);
    try {
      const { data } = await api.post("/consent/parental/verify", {
        token,
        child_first_name: childName.trim(),
        attesting_adult_name: adultName.trim(),
        attest_age_18: true,
        attest_text_accepted: true,
      });
      setConsentId(data?.consent_id || null);
      setStage("success");
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || err.message;
      if (status === 400) {
        // Wrong KBA — token NOT burned, show inline under child name field.
        setChildNameError(typeof msg === "string" ? msg : "That doesn't match — please try again.");
      } else if (status === 403) {
        setStage("wrong_account");
      } else if (status === 404) {
        setStage("broken");
      } else if (status === 409) {
        setStage("already_used");
      } else if (status === 410) {
        setStage("expired");
      } else {
        setGenericError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const continueAfterSuccess = () => {
    let dest = "/profiles";
    try {
      const raw = localStorage.getItem("parental_consent_return");
      if (raw) {
        let parsed;
        try { parsed = JSON.parse(raw); } catch (_e) { parsed = { path: raw, ts: 0 }; }
        // Legacy bare-string entries (ts=0) are honored once for back-compat.
        const ts = Number(parsed.ts) || 0;
        const fresh = ts === 0 || (Date.now() - ts) < (60 * 60 * 1000);
        if (fresh && typeof parsed.path === "string" && parsed.path.startsWith("/")) {
          dest = parsed.path;
        }
      }
      localStorage.removeItem("parental_consent_return");
    } catch (_e) { /* private mode */ }
    navigate(dest);
  };

  return (
    <div className="min-h-screen bg-[color:var(--cream)] flex items-center justify-center p-5" data-testid="parental-consent-verify-page">
      <div className="brutal-card bg-white p-6 max-w-lg w-full">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl border-[3px] border-[color:var(--ink)] flex items-center justify-center bg-[color:var(--coral)]">
            <ShieldCheck size={20} className="text-white" />
          </div>
          <div>
            <h1 className="display text-2xl font-extrabold leading-tight">Parental consent</h1>
            <p className="text-xs text-[color:var(--ink-soft)]">SonSpeak · COPPA verifiable consent</p>
          </div>
        </div>

        {stage === "broken" && (
          <BrokenState />
        )}
        {stage === "expired" && (
          <ExpiredState />
        )}
        {stage === "already_used" && (
          <AlreadyUsedState onContinue={continueAfterSuccess} />
        )}
        {stage === "wrong_account" && (
          <WrongAccountState />
        )}
        {stage === "success" && (
          <SuccessState consentId={consentId} onContinue={continueAfterSuccess} />
        )}
        {stage === "form" && (
          <form onSubmit={submit} className="space-y-4" data-testid="parental-consent-verify-form">
            <p className="text-sm leading-relaxed">
              Three short questions to confirm parental consent for your
              child's SonSpeak profile.
            </p>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-bold">
                1. Your child's first name
              </span>
              <input
                type="text"
                className="brutal-input w-full mt-1"
                value={childName}
                onChange={(e) => { setChildName(e.target.value); setChildNameError(""); }}
                required
                maxLength={80}
                disabled={busy}
                data-testid="verify-child-name"
                autoFocus
              />
              {childNameError && (
                <div className="text-xs text-[color:var(--coral-deep)] mt-1 flex items-center gap-1.5" data-testid="verify-child-name-error">
                  <AlertTriangle size={12} /> {childNameError}
                </div>
              )}
            </label>

            <label className="block">
              <span className="text-[11px] uppercase tracking-wider font-bold">
                2. Your full legal name
              </span>
              <input
                type="text"
                className="brutal-input w-full mt-1"
                value={adultName}
                onChange={(e) => setAdultName(e.target.value)}
                placeholder="As it appears on a government ID"
                required
                minLength={2}
                maxLength={200}
                disabled={busy}
                data-testid="verify-adult-name"
              />
            </label>

            <div className="brutal-card bg-[color:var(--paper-muted,#F2EFE7)] p-3 space-y-2">
              <span className="text-[11px] uppercase tracking-wider font-bold">3. Attestation</span>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={attestAge}
                  onChange={(e) => setAttestAge(e.target.checked)}
                  className="mt-1"
                  disabled={busy}
                  data-testid="verify-attest-age"
                />
                <span>I am 18 years of age or older.</span>
              </label>
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={attestText}
                  onChange={(e) => setAttestText(e.target.checked)}
                  className="mt-1"
                  disabled={busy}
                  data-testid="verify-attest-text"
                />
                <span className="text-xs leading-relaxed">{ATTESTATION}</span>
              </label>
            </div>

            {genericError && (
              <div className="text-xs text-[color:var(--coral-deep)] flex items-center gap-1.5">
                <AlertTriangle size={12} /> {genericError}
              </div>
            )}

            <button
              type="submit"
              className="pill-btn primary w-full justify-center"
              disabled={busy}
              data-testid="verify-submit"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              {busy ? "Recording consent…" : "Confirm parental consent"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function SuccessState({ consentId, onContinue }) {
  return (
    <div data-testid="verify-success">
      <div className="text-6xl text-center mb-3" aria-hidden>✅</div>
      <h2 className="display text-2xl font-extrabold text-center mb-2">Consent recorded.</h2>
      <p className="text-sm text-center text-[color:var(--ink-soft)] mb-4 leading-relaxed">
        Thank you. Your verifiable parental consent is on file. You can now
        create your child's profile.
      </p>
      {consentId && (
        <p className="text-[11px] text-center text-[color:var(--ink-soft)] mb-4 font-mono">
          Reference: {consentId.slice(0, 8)}…
        </p>
      )}
      <button
        type="button"
        onClick={onContinue}
        className="pill-btn primary w-full justify-center"
        data-testid="verify-continue"
      >
        Continue <ArrowRight size={14} />
      </button>
    </div>
  );
}

function BrokenState() {
  return (
    <div data-testid="verify-broken">
      <div className="text-5xl text-center mb-3" aria-hidden>🔗</div>
      <h2 className="display text-xl font-extrabold text-center mb-2">This link looks broken.</h2>
      <p className="text-sm text-center text-[color:var(--ink-soft)] mb-4 leading-relaxed">
        It may be from an old email or been copied incomplete. Please request
        a fresh verification link from your profile page.
      </p>
      <Link to="/profiles" className="pill-btn primary w-full justify-center" data-testid="verify-broken-back">
        Back to profiles <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function ExpiredState() {
  return (
    <div data-testid="verify-expired">
      <div className="text-5xl text-center mb-3" aria-hidden>⌛</div>
      <h2 className="display text-xl font-extrabold text-center mb-2">This link has expired.</h2>
      <p className="text-sm text-center text-[color:var(--ink-soft)] mb-4 leading-relaxed">
        Verification links are valid for 7 days. Please start the consent
        flow again from your profile page — it only takes a minute.
      </p>
      <Link to="/profiles" className="pill-btn primary w-full justify-center" data-testid="verify-expired-back">
        Back to profiles <ArrowRight size={14} />
      </Link>
    </div>
  );
}

function AlreadyUsedState({ onContinue }) {
  return (
    <div data-testid="verify-already-used">
      <div className="text-5xl text-center mb-3" aria-hidden>✓</div>
      <h2 className="display text-xl font-extrabold text-center mb-2">You're all set.</h2>
      <p className="text-sm text-center text-[color:var(--ink-soft)] mb-4 leading-relaxed">
        This link was already used. Parental consent is on file for your
        account.
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="pill-btn primary w-full justify-center"
        data-testid="verify-already-used-continue"
      >
        Continue <ArrowRight size={14} />
      </button>
    </div>
  );
}

function WrongAccountState() {
  return (
    <div data-testid="verify-wrong-account">
      <div className="text-5xl text-center mb-3" aria-hidden>🔒</div>
      <h2 className="display text-xl font-extrabold text-center mb-2">Wrong account.</h2>
      <p className="text-sm text-center text-[color:var(--ink-soft)] mb-4 leading-relaxed">
        This verification link belongs to a different SonSpeak account.
        Please sign in as the parent who started the verification.
      </p>
      <Link to="/login" className="pill-btn primary w-full justify-center" data-testid="verify-wrong-account-signin">
        Sign in <ArrowRight size={14} />
      </Link>
    </div>
  );
}
